import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppDispatch } from 'app/hooks';
import FormattedMessage from 'components/formatters/FormattedMessage';
import LoadingSpinner from 'components/icons/LoadingSpinner';
import {
  fetchAppSettingsWithRetryAsync,
  setSettingsInitialized,
} from 'features/admin/appSettingsSlice';
import { fetchPagesAsync } from 'features/page/pageSlice';
import { fetchSiteAsync } from 'features/site/siteSlice';
import api from 'helpers/api';
import { VERIFY_MAINTENANCE_INTERVAL } from 'helpers/consts';
import { Outlet } from 'react-router-dom';
import { useAppSettings } from 'selectors/appSettingsSelectors';
import { useUser } from 'selectors/userSelectors';

/**
 * AppStateManager - Manages core app state and initialization
 *
 * Responsibilities:
 * - Initializes app settings and configuration
 * - Polls backend for maintenance mode status (every 5 seconds)
 * - Displays maintenance screen when enabled (except on admin maintenance page)
 * - Manages application loading and error states
 * - Handles AsyncAPI WebSocket connection
 * - Flushes UI state on page unload
 * - Provides outlet for child routes
 */
export default function AppStateManager() {
  const dispatch = useAppDispatch();
  const { isSettingsInitialized, status, connectionError } = useAppSettings();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const initialCheckDone = useRef(false);
  const location = useLocation();
  const { user, userIsAdmin } = useUser();

  // Fetch App settings at the app level
  useEffect(() => {
    if (!isSettingsInitialized) {
      dispatch(fetchAppSettingsWithRetryAsync());
    }
  }, [isSettingsInitialized, dispatch]);

  // Maintenance mode polling
  // Check if current route is admin maintenance page
  const isAdminMaintenancePage = location.pathname === '/admin/maintenance';
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const response = await api.get('admin/maintenance');
        const currentMaintenanceMode = response.data.maintenanceMode;
        // Only reload if this is a change from the initial state (not the initial check)
        if (
          initialCheckDone.current &&
          currentMaintenanceMode !== isMaintenanceMode &&
          !isAdminMaintenancePage &&
          !userIsAdmin
        ) {
          console.log(
            `Maintenance mode changed to ${currentMaintenanceMode}, reloading...`,
          );
          window.location.reload();
        }
        setIsMaintenanceMode(currentMaintenanceMode);
        initialCheckDone.current = true;
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
      }
    };
    // Initial check on mount
    checkMaintenanceMode();
    // Set up polling interval
    const pollInterval = setInterval(
      checkMaintenanceMode,
      VERIFY_MAINTENANCE_INTERVAL,
    );
    // Cleanup on unmount
    return () => clearInterval(pollInterval);
  }, [isAdminMaintenancePage]);

  // App Settings
  // Retry timer - resets settings to trigger a new fetch if app settings status is 'failed'
  useEffect(() => {
    if (status === 'failed') {
      const timeoutId = setTimeout(() => {
        dispatch(setSettingsInitialized(false));
      }, 1000 * 30); // 30 seconds
      return () => clearTimeout(timeoutId);
    }
  }, [status, dispatch]);

  // Fetch site data once on app start
  useEffect(() => {
    // Fetch pages first, for quick loading of the homepage and to get the page slugs for routing
    dispatch(fetchPagesAsync());
    // Then fetch the rest of the site data
    dispatch(fetchSiteAsync(user ? { forUsers: true } : {}));
    // eslint-disable-next-line
  }, [user]);

  // Show maintenance screen only if maintenance mode is active AND not on admin page
  if (isMaintenanceMode && !userIsAdmin && !(isAdminMaintenancePage && !user)) {
    return (
      <LoadingSpinner active={true} onDark={true}>
        <FormattedMessage id="NAVIGATION.LOADER.MAINTENANCE" />
      </LoadingSpinner>
    );
  }

  // Don't render routes until app settings are initialized
  if (!isSettingsInitialized) {
    return (
      <LoadingSpinner active={true} onDark={true}>
        <FormattedMessage id="NAVIGATION.LOADER.APPLICATION" />
      </LoadingSpinner>
    );
  }

  if (status === 'failed' || connectionError) {
    if (connectionError) console.log('Connection error', connectionError);
    return (
      <LoadingSpinner active={true} onDark={true}>
        <FormattedMessage id="NAVIGATION.LOADER.UNAVAILABLE" />
      </LoadingSpinner>
    );
  }

  if (isMaintenanceMode)
    return (
      <div className="maintenance_mode">
        <Outlet />
      </div>
    );
  return <Outlet />;
}
